import React, { FC } from "react";
import { Badge, Text, Heading, Flex, Icon, IconName } from "@telia/teddy";
import cn from "classnames";
import "./CollapsedMemberCard.less";
import { getFormattedPrice } from "../../../../../shared/priceFormatter";

interface MemberCardInfoProps {
    discountPrice?: number;
    familyDiscountBadgeOverride?: string;
    isCompleted?: boolean;
    isExisting?: boolean;
    price?: string;
    shouldGetFamilyDiscount?: boolean;
    subscriptionName: string;
    title?: string;
}

export const MemberCardInfo: FC<MemberCardInfoProps> = ({
    title,
    isCompleted,
    isExisting,
    subscriptionName,
    price,
    discountPrice,
    shouldGetFamilyDiscount,
    familyDiscountBadgeOverride,
}) => {
    return (
        <div
            className={cn("collapsed-member-card__collapser-info-wrapper", {
                "collapsed-member-card__collapser-info-wrapper--existing": isExisting,
            })}
        >
            <div className="collapsed-member-card__badge-wrapper">
                {isCompleted && !isExisting && (
                    <Badge className="collapsed-member-card__badge-item" variant="success">
                        Ferdig
                    </Badge>
                )}
                {isCompleted && isExisting && (
                    <Badge className="collapsed-member-card__badge-item" variant="information">
                        Eksisterende
                    </Badge>
                )}
                {isCompleted && shouldGetFamilyDiscount && (
                    <Badge className="collapsed-member-card__badge-item" variant="special">
                        {familyDiscountBadgeOverride || "Familierabatt"}
                    </Badge>
                )}
            </div>
            {title && (
                <div className="collapsed-member-card__collapser-info" data-di-mask>
                    <Text className="collapsed-member-card__collapser-info-title">{title}</Text>
                </div>
            )}
            <Heading mb="100" as="h3" variant="title-100" className="collapsed-member-card__collapser-heading">
                {subscriptionName}
            </Heading>
            {isCompleted && (
                <Flex>
                    {!!discountPrice && getFormattedPrice(discountPrice) !== price && (
                        <Text className="collapsed-member-card__discount-price">
                            {getFormattedPrice(discountPrice)}
                        </Text>
                    )}
                    {!!price && <Text>{price}/md.</Text>}
                </Flex>
            )}
        </div>
    );
};

interface MemberCardButtonProps {
    buttonIcon: IconName;
    buttonLabel: string;
    onButtonClick: () => void;
}

export const MemberCardButton: FC<MemberCardButtonProps> = ({ onButtonClick, buttonIcon, buttonLabel }) => (
    <button className="collapsed-member-card__edit-btn" onClick={onButtonClick}>
        <span className="collapsed-member-card__edit-btn-icon">
            <Icon name={buttonIcon} />
        </span>
        <span className="collapsed-member-card__edit-btn-text">{buttonLabel}</span>
    </button>
);

interface Props {
    buttonIcon: IconName;
    buttonLabel: string;
    discountPrice?: number;
    formatter?: (phoneNumber: string) => string;
    hasProductUser?: boolean;
    isCompleted?: boolean;
    needsPhoneNumber: boolean;
    onButtonClick: () => void;
    price?: string;
    productUserFullName?: string;
    productUserPhoneNumber?: string;
    shouldGetFamilyDiscount?: boolean;
    subscriptionName: string;
}

const CollapsedMemberCard: FC<Props> = ({
    hasProductUser,
    subscriptionName,
    productUserFullName,
    productUserPhoneNumber,
    buttonIcon,
    buttonLabel,
    needsPhoneNumber,
    shouldGetFamilyDiscount,
    price,
    discountPrice,
    isCompleted,
    onButtonClick,
    formatter = (number) => number,
}) => {
    const hasFilledOutUser = !needsPhoneNumber
        ? !!productUserFullName
        : !!productUserPhoneNumber && !!productUserFullName;

    const title = hasFilledOutUser
        ? `${productUserFullName}${productUserPhoneNumber ? ` - ${formatter(productUserPhoneNumber)}` : ""}`
        : "Bruker ikke valgt enda";

    return (
        <div className="collapsed-member-card__collapser-chooser-wrapper">
            <div className="collapsed-member-card__collapser-left-wrapper">
                <MemberCardInfo
                    title={title}
                    subscriptionName={subscriptionName}
                    price={price}
                    discountPrice={discountPrice}
                    shouldGetFamilyDiscount={shouldGetFamilyDiscount}
                    isCompleted={isCompleted}
                />
            </div>
            {hasProductUser && (
                <MemberCardButton buttonIcon={buttonIcon} buttonLabel={buttonLabel} onButtonClick={onButtonClick} />
            )}
        </div>
    );
};

export default CollapsedMemberCard;
